/**
 * Teams List Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Grid of team cards with loading and empty states.
 */

import React from 'react';
import { Users } from 'lucide-react';
import type { Team } from '../../../types/admin';
import TeamCard from './TeamCard';

interface TeamsListProps {
  teams: Team[];
  isLoading?: boolean;
  onEdit: (team: Team) => void;
  onManageMembers: (team: Team) => void;
  onViewClients: (team: Team) => void;
  onDelete: (team: Team) => void;
}

// Skeleton loader for team cards
const TeamCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-200" />
        <div>
          <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-40 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="w-6 h-6 bg-gray-200 rounded" />
    </div>
    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-6">
      <div className="h-3 w-20 bg-gray-200 rounded" />
      <div className="h-3 w-16 bg-gray-200 rounded" />
    </div>
  </div>
);

const TeamsList: React.FC<TeamsListProps> = ({
  teams,
  isLoading = false,
  onEdit,
  onManageMembers,
  onViewClients,
  onDelete,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <TeamCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (teams.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Users size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No teams found</h3>
        <p className="text-gray-500">
          Create a team to organize users and manage client access.
        </p>
      </div>
    );
  }

  // Teams grid
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {teams.map((team) => (
        <TeamCard
          key={team.id}
          team={team}
          onEdit={onEdit}
          onManageMembers={onManageMembers}
          onViewClients={onViewClients}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default TeamsList;
